import React, { Fragment, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, IconButton, Skeleton, Stack, Typography, colors } from '@mui/material';
import { DraftPolicy, DraftManual } from 'views/App/Portal/Policy/_types/manuals/draft';
import parse, { DOMNode } from 'html-react-parser';
import { Download } from '@mui/icons-material';
import { useSearchContext } from '../context';
import { ContentPage } from '../styles';
import { warning } from '_lex/_foundations/phxColors';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import draftManualHelper from 'views/App/Portal/Policy/DraftManuals/helpers/draftManualHelper';

function DraftManualsSearchPolicy({
  manualId,
  policy,
  manual,
  scrollId,
}: {
  manualId: string;
  policy?: DraftPolicy;
  manual?: DraftManual;
  scrollId?: string;
}) {
  const [params] = useSearchParams();
  const { handleDownload, isContentLoading } = useSearchContext();

  useEffect(() => {
    if (scrollId && policy) {
      const element = document.getElementById(`polsec_${scrollId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [scrollId, policy, isContentLoading]);

  if (!policy) {
    return (
      <ContentPage>
        <Stack spacing={2}>
          <Skeleton
            variant='text'
            width='60%'
            height={32}
          />
          <Skeleton
            variant='text'
            width='40%'
            height={24}
          />
          <Skeleton
            variant='rectangular'
            height={200}
          />
        </Stack>
      </ContentPage>
    );
  }

  // Format policy title with proper numbering if manual data is available
  const formattedPolicyTitle = manual ? draftManualHelper.formatManualNodeTitle(policy, manual) : policy.title;

  return (
    <ContentPage>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Stack spacing={1}>
            <Typography variant='h5'>{formattedPolicyTitle}</Typography>
            <Typography
              variant='body2'
              color='text.secondary'
            >
              {policy.effectiveDate && `Effective Date: ${policy.effectiveDate}`}
            </Typography>
          </Stack>
          {/* TODO: Add download button 
          <IconButton
            onClick={() => handleDownload(manualId, policy.id, 'POLICY', policy.title)}
            sx={{ color: 'primary.main' }}
          >
            <Download />
          </IconButton>*/}
        </Box>

        {policy.sections?.map((section) => {
          // Format section title with proper numbering if manual data is available
          const formattedSectionTitle = manual
            ? draftManualHelper.formatPolicyNodeTitle(
                section,
                draftManualHelper.getNumberManualNode(policy, manual),
                policy
              )
            : section.title;

          return (
            <Fragment key={section.id}>
              <Box
                id={`polsec_${section.id}`}
                sx={{
                  backgroundColor: scrollId === section.id ? warning[100] : 'transparent',
                  p: 2,
                  borderRadius: 1,
                  border: scrollId === section.id ? `2px solid ${warning[300]}` : 'none',
                }}
              >
                <Typography
                  variant='h6'
                  sx={{ mb: 2 }}
                >
                  {formattedSectionTitle}
                </Typography>
                <Box sx={{ '& p': { mb: 2 } }}>
                  {parse(section.content, {
                    replace: (domNode: DOMNode) => {
                      if (
                        domNode.type === 'tag' &&
                        domNode.name === 'span' &&
                        domNode.attribs?.class === 'text-entity'
                      ) {
                        return (
                          <span
                            key={Math.random()}
                            className='text-entity'
                            style={{
                              padding: '2px 4px',
                              borderRadius: 8,
                              display: 'inline-flex',
                            }}
                          >
                            {domNode.children?.[0] && 'data' in domNode.children[0]
                              ? (domNode.children[0] as any).data
                              : ''}
                          </span>
                        );
                      }
                    },
                  })}
                </Box>

                {section.subSections?.map((subSection) => {
                  // Format subsection title with proper numbering if manual data is available
                  const formattedSubSectionTitle = manual
                    ? draftManualHelper.formatPolicyNodeTitle(
                        subSection,
                        draftManualHelper.getNumberManualNode(policy, manual),
                        policy
                      )
                    : subSection.title;

                  return (
                    <Box
                      key={subSection.id}
                      id={`polsec_${subSection.id}`}
                      sx={{
                        mt: 2,
                        pl: 3,
                        backgroundColor: scrollId === subSection.id ? warning[100] : 'transparent',
                        p: 2,
                        borderRadius: 1,
                        border: scrollId === subSection.id ? `2px solid ${warning[300]}` : 'none',
                      }}
                    >
                      <Typography
                        variant='subtitle1'
                        sx={{ mb: 2 }}
                      >
                        {formattedSubSectionTitle}
                      </Typography>
                      <Box sx={{ '& p': { mb: 2 } }}>
                        {parse(subSection.content, {
                          replace: (domNode: DOMNode) => {
                            if (
                              domNode.type === 'tag' &&
                              domNode.name === 'span' &&
                              domNode.attribs?.class === 'text-entity'
                            ) {
                              return (
                                <span
                                  key={Math.random()}
                                  className='text-entity'
                                  style={{
                                    padding: '2px 4px',
                                    borderRadius: 8,
                                    display: 'inline-flex',
                                  }}
                                >
                                  {domNode.children?.[0] && 'data' in domNode.children[0]
                                    ? (domNode.children[0] as any).data
                                    : ''}
                                </span>
                              );
                            }
                          },
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Fragment>
          );
        })}
      </Stack>
    </ContentPage>
  );
}

export default DraftManualsSearchPolicy;
